/**
 * useTelemetry (P184) -- the live service-health feed (the twin's third
 * leg). useSampleTelemetry triggers a synthetic sample (admin) + refreshes.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import type {
  ServiceHistory,
  TelemetryReport,
  TelemetrySampleResult,
} from "shared/api/types";

const telemetryKeys = {
  all: ["twin", "telemetry"] as const,
  history: (id: string) => ["twin", "telemetry", "history", id] as const,
};

/** Poll cadence for a live-lens that's open (matches the cascade feed). */
const TELEMETRY_POLL_MS = 15_000;

/**
 * @param poll when true, auto-refetch every 15s so a live overlay stays
 * current while it's open (the constellation/capmap "live" lens passes
 * `lens === "live"`); false = fetch-once, the default for other callers.
 */
export function useTelemetry(poll = false): UseQueryResult<TelemetryReport, ApiError> {
  return useQuery({
    queryKey: telemetryKeys.all,
    queryFn: () => api.get<TelemetryReport>("/v1/twin/telemetry"),
    refetchInterval: poll ? TELEMETRY_POLL_MS : false,
    refetchIntervalInBackground: false,
  });
}

/** P227: the per-service multi-metric history, behind the drill-in chart.
 * Lazy -- enabled only when a row is expanded. */
export function useServiceHistory(
  serviceId: string | null,
): UseQueryResult<ServiceHistory, ApiError> {
  return useQuery({
    queryKey: telemetryKeys.history(serviceId ?? ""),
    queryFn: () => api.get<ServiceHistory>(`/v1/twin/telemetry/${serviceId}/history`),
    enabled: Boolean(serviceId),
  });
}

export function useSampleTelemetry(): UseMutationResult<
  TelemetrySampleResult,
  ApiError,
  void
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: () =>
      api.post<TelemetrySampleResult>("/v1/admin/twin/telemetry/sample", {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: telemetryKeys.all });
    },
  });
}
